import { unwrapResult } from '@reduxjs/toolkit';

const MAX_IMAGE_PIXELS = 20_000_000; // ~20MP cap to bound base64 embed size

function resolveImageSrc(src) {
    if (!src) {
        return Promise.reject(new Error("Missing image source"));
    }
    if (src.startsWith('blob:')) {
        return fetch(src)
            .then(r => r.blob())
            .then(blob => new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            }));
    }
    if (/^(https?:\/\/|data:image\/)/i.test(src)) {
        return Promise.resolve(src);
    }
    return Promise.reject(new Error("Unsupported image source"));
}

// Encapsulates turning a drop event (dropped file or dragged <img> HTML) into a
// new Creature. Args:
//   createCreature   — dispatches createEntityAsync for a Creature
//   alert            — user feedback callback
//   onCreatureAdded  — called with the created creature (e.g. to select it)
export function useCreatureDrop({ createCreature, alert, onCreatureAdded }) {
    function createCreatureFromSrcAndName(src, name) {
        return new Promise((resolve, reject) => {
            var img = new Image();
            img.onload = function () {
                if (this.width * this.height > MAX_IMAGE_PIXELS) {
                    reject(new Error("Image is too large to use as a creature"));
                    return;
                }
                const cleanName = (name || "").trim() || "New Creature";
                createCreature({ src, name: cleanName, srcWidth: this.width, srcHeight: this.height, size: 1, heightRatio: this.height / this.width })
                    .then(unwrapResult)
                    .then(response => {
                        if (onCreatureAdded) onCreatureAdded(response);
                        alert("Added " + response.name);
                        resolve(response);
                    });
            };
            img.onerror = () => reject(new Error("Failed to load image"));
            img.src = src;
        });
    }

    function addNewCreatureFromDropEvent(e) {
        e.preventDefault();

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            if (!file.type.startsWith('image/')) return Promise.resolve(null);
            const name = file.name.replace(/\.[^.]+$/, '');
            return new Promise(resolve => {
                const reader = new FileReader();
                reader.onload = evt => createCreatureFromSrcAndName(evt.target.result, name).then(resolve)
                    .catch(err => { alert(err.message || "Couldn't add that image — try a different one"); resolve(null); });
                reader.readAsDataURL(file);
            });
        }

        if (e.dataTransfer.types.includes("text/html")) {
            const imgHtml = e.dataTransfer.getData("text/html");
            var parser = new DOMParser();
            var htmlDoc = parser.parseFromString(imgHtml, 'text/html');
            const imgElem = htmlDoc.getElementsByTagName("img")[0];
            if (!imgElem) return Promise.resolve(null);
            var alt = imgElem.getAttribute("alt");
            const rawSrc = imgElem.getAttribute("src");
            if (!alt) {
                try {
                    const url = new URL(rawSrc);
                    const path = url.pathname.split('/');
                    if (path) alt = path[path.length - 1];
                    if (alt.includes('.')) {
                        const arr = alt.split('.');
                        arr.pop();
                        alt = decodeURIComponent(arr.join('.'));
                    }
                } catch (_) {}
            }
            return resolveImageSrc(rawSrc)
                .then(src => createCreatureFromSrcAndName(src, alt))
                .catch(err => { alert(err.message || "Couldn't add that image — try a different one"); return null; });
        }

        return Promise.resolve(null);
    }

    return addNewCreatureFromDropEvent;
}
